import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { linesMatrixIntegrationApi } from 'services/apiDigitalTicket/linesMatrixIntegration';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATHS from 'constants/basePath';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as LinesMatrixIntegrationActions from './actions';

// Types
import { LinesMatrixIntegrationTypes } from './types';

function* list({
  payload,
}: ReturnType<
  typeof LinesMatrixIntegrationActions.listLinesMatrixIntegrationRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando integrações' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(linesMatrixIntegrationApi.list, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        LinesMatrixIntegrationActions.listLinesMatrixIntegrationSuccess({
          linesMatrixIntegrationList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - listLines - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(
      LinesMatrixIntegrationActions.listLinesMatrixIntegrationFailure(),
    );
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
  meta,
}: ReturnType<
  typeof LinesMatrixIntegrationActions.createLineMatrixIntegrationRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando integrações' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesMatrixIntegrationApi.create, {
      data: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        LinesMatrixIntegrationActions.createLineMatrixIntegrationSuccess({
          lineMatrixIntegrationData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Integrações criadas com sucesso',
        }),
      );

      yield meta.promise.resolve(response);

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - createLine - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(
      LinesMatrixIntegrationActions.createLineMatrixIntegrationFailure(),
    );

    yield meta.promise.reject(error);

    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
  meta,
}: ReturnType<
  typeof LinesMatrixIntegrationActions.updateLineMatrixIntegrationRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando integrações' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(linesMatrixIntegrationApi.update, {
      data: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        LinesMatrixIntegrationActions.updateLineMatrixIntegrationSuccess(),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Integrações editadas com sucesso',
        }),
      );

      yield meta.promise.resolve(response);

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - updateLine - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(
      LinesMatrixIntegrationActions.updateLineMatrixIntegrationFailure(),
    );

    yield meta.promise.reject(error);

    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<
  typeof LinesMatrixIntegrationActions.deleteLineMatrixIntegrationRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo integrações' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(linesMatrixIntegrationApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        LinesMatrixIntegrationActions.deleteLineMatrixIntegrationSuccess(),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Integrações excluídas com sucesso',
        }),
      );
      yield put(
        LinesMatrixIntegrationActions.listLinesMatrixIntegrationRequest({
          _includes: 'startlinesection,endlinesection',
        }),
      );
      yield put(DialogsActions.setClose());
      window.location.replace(PATHS.LINE_MATRIX.BASE_URL);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - removeLine - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(
      LinesMatrixIntegrationActions.deleteLineMatrixIntegrationFailure(),
    );
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const linesMatrixIntegrationSagas = [
  takeLatest(
    LinesMatrixIntegrationTypes.LIST_LINES_MATRIX_INTEGRATION_REQUEST,
    list,
  ),
  takeLatest(
    LinesMatrixIntegrationTypes.CREATE_LINE_MATRIX_INTEGRATION_REQUEST,
    create,
  ),
  takeLatest(
    LinesMatrixIntegrationTypes.UPDATE_LINE_MATRIX_INTEGRATION_REQUEST,
    update,
  ),
  takeLatest(
    LinesMatrixIntegrationTypes.DELETE_LINE_MATRIX_INTEGRATION_REQUEST,
    remove,
  ),
];
